import React, { useCallback, useEffect, useRef, useState } from 'react';
import Globe, { type GlobeMethods } from 'react-globe.gl';
import { motion } from 'framer-motion';
import { MousePointer2 } from 'lucide-react';
import { globeAssets, GLOBE_POINT_ALTITUDE } from './constants';
import { disposeCityLabelSprites, getCityLabelSprite } from './globeLabelSprite';
import {
  createDayNightGlobeMaterial,
  formatIranTime,
  updateDayNightUniforms,
} from './globeDayNightMaterial';
import {
  setCloudsSpinPaused,
  setupRealisticGlobeScene,
  syncSunLighting,
} from './globeRealisticSetup';
import { toGlobeArc, toGlobePoint } from './globeAccessors';
import type { CompanionCity, GlobeArc, GlobePoint } from './types';

const FRAME_PADDING = 28;
const SUN_TICK_MS = 30_000;

type CompanionsGlobeProps = {
  activeCity: CompanionCity;
  pointData: GlobePoint[];
  arcData: GlobeArc[];
  onSelectCity: (city: CompanionCity | null | undefined) => void;
};

const CompanionsGlobe: React.FC<CompanionsGlobeProps> = ({
  activeCity,
  pointData,
  arcData,
  onSelectCity,
}) => {
  const globeRef = useRef<GlobeMethods | undefined>(undefined);
  const frameRef = useRef<HTMLDivElement>(null);
  const teardownSceneRef = useRef<(() => void) | null>(null);
  const globeReadyRef = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globeMaterialRef = useRef<any>(null);
  const [globeSize, setGlobeSize] = useState(320);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [globeMaterial, setGlobeMaterial] = useState<any>(null);
  const [iranTimeLabel, setIranTimeLabel] = useState(() => formatIranTime());

  const isHoveringCityRef = useRef(false);

  const handleCityHover = useCallback((item: object | null) => {
    const hovering = item != null;
    if (isHoveringCityRef.current === hovering) return;
    isHoveringCityRef.current = hovering;

    document.body.style.cursor = hovering ? 'pointer' : '';
    const controls = globeRef.current?.controls();
    if (controls) {
      controls.autoRotate = !hovering;
    }
    setCloudsSpinPaused(hovering);
  }, []);

  const pointAltitudeFor = useCallback(
    (d: object) => {
      const point = toGlobePoint(d);
      return point.id === activeCity.id
        ? GLOBE_POINT_ALTITUDE.active
        : GLOBE_POINT_ALTITUDE.default;
    },
    [activeCity.id],
  );

  const objectThreeObjectForPoint = useCallback(
    (d: object) => {
      const point = toGlobePoint(d);
      return getCityLabelSprite(point.id, point.id === activeCity.id);
    },
    [activeCity.id],
  );

  const refreshSun = useCallback(() => {
    const now = new Date();
    setIranTimeLabel(formatIranTime(now));
    if (globeMaterialRef.current) {
      const pov = globeRef.current?.pointOfView();
      updateDayNightUniforms(
        globeMaterialRef.current,
        now,
        pov ? { lng: pov.lng, lat: pov.lat } : undefined,
      );
    }
    if (globeRef.current) {
      syncSunLighting(globeRef.current, now);
    }
  }, []);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const syncSize = () => {
      const side = Math.min(el.clientWidth, el.clientHeight) - FRAME_PADDING;
      setGlobeSize(Math.max(220, Math.floor(side)));
    };

    syncSize();
    const observer = new ResizeObserver(syncSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!activeCity || !globeRef.current) return;
    globeRef.current.pointOfView(
      { lat: activeCity.lat, lng: activeCity.lng, altitude: 1.55 },
      1100,
    );
  }, [activeCity]);

  useEffect(() => {
    refreshSun();
    const id = window.setInterval(refreshSun, SUN_TICK_MS);
    return () => window.clearInterval(id);
  }, [refreshSun]);

  useEffect(
    () => () => {
      document.body.style.cursor = '';
      isHoveringCityRef.current = false;
      setCloudsSpinPaused(false);
      const controls = globeRef.current?.controls();
      if (controls) controls.autoRotate = true;
      disposeCityLabelSprites();
      teardownSceneRef.current?.();
      teardownSceneRef.current = null;
      globeMaterialRef.current = null;
    },
    [],
  );

  const handleGlobeReady = () => {
    const globe = globeRef.current;
    if (!globe || globeReadyRef.current) return;
    globeReadyRef.current = true;

    const controls = globe.controls();
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableRotate = true;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.28;
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;

    void (async () => {
      const THREE = await import('three');
      const material = await createDayNightGlobeMaterial(THREE);
      globeMaterialRef.current = material;
      setGlobeMaterial(material);
      refreshSun();

      teardownSceneRef.current?.();
      teardownSceneRef.current = await setupRealisticGlobeScene(globe);
      refreshSun();
    })();
  };

  const handleZoom = useCallback(() => {
    refreshSun();
  }, [refreshSun]);

  const makeRingColor = (d: GlobePoint) => (t: number) => {
    const isActive = d.id === activeCity.id;
    const alpha = (isActive ? 0.55 : 0.22) * (1 - t);
    return isActive ? `rgba(125,211,252,${alpha})` : `rgba(129,140,248,${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.06 }}
      className="companions-hero-frame companions-hero-globe-frame w-full"
    >
      <div
        ref={frameRef}
        className="companions-globe-frame companions-globe-frame--realistic relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[var(--cp-radius-xl)] p-3 sm:p-3.5"
      >
        <div className="companions-globe-frame__stars pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute left-3 top-3 z-20 rounded-lg border border-white/10 bg-slate-950/55 px-2.5 py-1 text-[10px] font-bold text-cyan-100/90 backdrop-blur-sm">
          روز و شب · {iranTimeLabel}
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center">
          <Globe
            ref={globeRef}
            width={globeSize}
            height={globeSize}
            backgroundColor="rgba(0,0,0,0)"
            backgroundImageUrl={globeAssets.background}
            globeMaterial={globeMaterial ?? undefined}
            globeImageUrl={globeMaterial ? undefined : globeAssets.day}
            bumpImageUrl={globeAssets.bump}
            globeCurvatureResolution={4}
            showAtmosphere
            atmosphereColor="#8ec8ff"
            atmosphereAltitude={0.14}
            onGlobeReady={handleGlobeReady}
            onZoom={handleZoom}
            objectsData={pointData}
            objectLat={(d: object) => toGlobePoint(d).lat}
            objectLng={(d: object) => toGlobePoint(d).lng}
            objectAltitude={pointAltitudeFor}
            objectFacesSurfaces={false}
            objectThreeObject={objectThreeObjectForPoint}
            onObjectClick={(obj) => onSelectCity(toGlobePoint(obj))}
            onObjectHover={(obj) => handleCityHover(obj)}
            arcsData={arcData}
            arcStartLat={(d: object) => toGlobeArc(d).startLat}
            arcStartLng={(d: object) => toGlobeArc(d).startLng}
            arcEndLat={(d: object) => toGlobeArc(d).endLat}
            arcEndLng={(d: object) => toGlobeArc(d).endLng}
            arcColor={(d: object) => toGlobeArc(d).color}
            arcAltitude={(d: object) => {
              const arc = toGlobeArc(d);
              return arc.active ? 0.28 : 0.16 + (arc.order % 4) * 0.012;
            }}
            arcStroke={(d: object) => (toGlobeArc(d).active ? 0.42 : 0.2)}
            arcDashLength={(d: object) => (toGlobeArc(d).active ? 0.35 : 0.55)}
            arcDashGap={(d: object) => (toGlobeArc(d).active ? 0.18 : 0.28)}
            arcDashAnimateTime={(d: object) => (toGlobeArc(d).active ? 1800 : 3200)}
            arcsTransitionDuration={600}
            arcCircularResolution={128}
            ringsData={pointData}
            ringLat={(d: object) => toGlobePoint(d).lat}
            ringLng={(d: object) => toGlobePoint(d).lng}
            ringColor={(d: object) => makeRingColor(toGlobePoint(d))}
            ringMaxRadius={(d: object) => (toGlobePoint(d).id === activeCity.id ? 7.5 : 4.2)}
            ringPropagationSpeed={(d: object) => (toGlobePoint(d).id === activeCity.id ? 3.4 : 2)}
            ringRepeatPeriod={(d: object) => (toGlobePoint(d).id === activeCity.id ? 900 : 1300)}
          />
        </div>

        <div className="pointer-events-none relative z-20 flex shrink-0 items-center justify-center gap-2 py-1 text-[10px] font-bold text-white/80">
          <MousePointer2 className="h-3 w-3 text-cyan-300" />
          بچرخانید · روی شهر کلیک کنید
        </div>

      </div>
    </motion.div>
  );
};

export default CompanionsGlobe;
